import { Controller } from "@hotwired/stimulus"

// Kept in sync with shared/_watchlist_creator.html.slim.
const CREATE_MODAL_ID = 'watchlist-create-modal'

export default class extends Controller {
  static targets = [
    "watchlistSelect",
    "selectAllCheckbox",
    "checkboxHeader",
    "checkboxCell",
    "stockCount",
    "table",
    "tableBody",
    "downloadButton",
    "keyboardHint",
    "createButton",
    "createLabel",
    "createModal",
    "createName",
    "createError",
    "createSubmit"
  ]

  static values = {
    watchlistId: Number,
    checkedTickers: Array,
    watchlists: Array
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Bind event handlers
    this.boundHandleNewRows = this.handleNewRows.bind(this)
    this.boundHandleKeyboardToggle = this.handleKeyboardToggle.bind(this)

    // Listen for new rows added by infinite scroll
    document.addEventListener('infinite-scroll:rows-added', this.boundHandleNewRows)

    // Listen for keyboard toggle events from lightweight-chart controller
    document.addEventListener('watchlist:toggle', this.boundHandleKeyboardToggle)

    this.adoptCreateModal()
  }

  disconnect() {
    document.removeEventListener('infinite-scroll:rows-added', this.boundHandleNewRows)
    document.removeEventListener('watchlist:toggle', this.boundHandleKeyboardToggle)
    this.releaseCreateModal()
  }

  // The create-watchlist dialog is authored inside the list header, which is
  // fine at rest but not while it is open: .card carries a transform (hover
  // lift / entrance animation), and a transformed ancestor becomes the
  // containing block for position: fixed — so Bootstrap would render the
  // dialog inside the card rather than centred over the viewport. It is
  // therefore parked on <body> for as long as it is open and put back
  // afterwards, which keeps Turbo's page-cache snapshot of the header intact.
  //
  // Living outside Stimulus' target and action scope while open is why the
  // elements are cached here and the submit listener is hand-wired.
  adoptCreateModal() {
    // Stimulus only scans within the controller element, so a re-connect while
    // the dialog is still parked on <body> (a DOM move, a Turbo restore that
    // skipped before-cache) finds no target — fall back to the id and re-adopt
    // the orphan rather than leaving the button inert.
    this.createModalElement = this.hasCreateModalTarget
      ? this.createModalTarget
      : document.getElementById(CREATE_MODAL_ID)
    if (!this.createModalElement) return

    // Home is where the dialog is authored: beside the button in the header.
    this.createModalHome = this.createModalElement.parentElement === document.body
      ? (this.hasCreateButtonTarget ? this.createButtonTarget.parentElement : null)
      : this.createModalElement.parentElement
    if (!this.createModalHome) return

    const find = name => this.createModalElement.querySelector(`[data-watchlist-selection-target="${name}"]`)
    this.createNameElement = find('createName')
    this.createErrorElement = find('createError')
    this.createSubmitElement = find('createSubmit')
    this.createFormElement = this.createModalElement.querySelector('form')

    this.boundCreateWatchlist = this.createWatchlist.bind(this)
    this.createFormElement?.addEventListener('submit', this.boundCreateWatchlist)

    this.boundReturnCreateModalHome = this.returnCreateModalHome.bind(this)
    document.addEventListener('turbo:before-cache', this.boundReturnCreateModalHome)
  }

  releaseCreateModal() {
    if (!this.createModalElement) return

    document.removeEventListener('turbo:before-cache', this.boundReturnCreateModalHome)
    this.createFormElement?.removeEventListener('submit', this.boundCreateWatchlist)
    this.forceHideCreateModal()
    window.bootstrap?.Modal?.getInstance(this.createModalElement)?.dispose()
    this.returnCreateModalHome()
    this.createModalElement = null
  }

  returnCreateModalHome() {
    if (!this.createModalElement || !this.createModalHome) return
    if (this.createModalElement.parentElement === this.createModalHome) return

    // Re-parenting an OPEN dialog would leave a shown modal nested back in the
    // header — and on turbo:before-cache that is exactly what Back restores.
    this.forceHideCreateModal()
    this.createModalHome.appendChild(this.createModalElement)
  }

  // Bootstrap's hide() finishes on a CSS transition, but turbo:before-cache
  // snapshots synchronously — so the residue (.show on the dialog, the
  // backdrop, body.modal-open and its scroll lock) has to be cleared by hand
  // or the cached page comes back dimmed and unscrollable.
  forceHideCreateModal() {
    const modal = this.createModalElement
    if (!modal) return

    window.bootstrap?.Modal?.getInstance(modal)?.hide()
    modal.classList.remove('show')
    modal.style.removeProperty('display')
    modal.setAttribute('aria-hidden', 'true')

    // Another dialog may legitimately own the backdrop and the scroll lock.
    if (document.querySelector('.modal.show')) return

    document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove())
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('padding-right')
  }

  // Called when watchlist dropdown changes
  async watchlistChanged(event) {
    const watchlistId = event.target.value

    if (!watchlistId) {
      // No watchlist selected - hide checkboxes
      this.watchlistIdValue = 0
      this.checkedTickersValue = []
      this.hideCheckboxes()
      this.updateStockCount(0)
      return
    }

    this.watchlistIdValue = parseInt(watchlistId)

    // Show checkboxes
    this.showCheckboxes()

    // Fetch tickers in this watchlist
    await this.fetchWatchlistTickers()
  }

  async fetchWatchlistTickers() {
    try {
      const response = await fetch(`/watchlists/${this.watchlistIdValue}/watchlist_stocks/tickers`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) throw new Error('Failed to fetch tickers')

      const data = await response.json()
      this.checkedTickersValue = data.tickers || []
      this.updateStockCount(data.count || 0)
      this.updateAllCheckboxes()
    } catch (error) {
      console.error('Error fetching watchlist tickers:', error)
    }
  }

  // --- Creating a watchlist without leaving the page -----------------------
  // The page around this list carries state a round trip to /watchlists would
  // throw away (filters, sort, selected ticker, loaded chart), so the modal
  // posts JSON and we splice the new option into the existing select.

  openCreateModal() {
    if (!this.createModalElement) return

    this.hideCreateError()
    if (this.createNameElement) this.createNameElement.value = ''

    document.body.appendChild(this.createModalElement)

    const modal = window.bootstrap?.Modal?.getOrCreateInstance(this.createModalElement)
    if (!modal) {
      this.returnCreateModalHome()
      return
    }

    this.createModalElement.addEventListener(
      'hidden.bs.modal',
      () => this.returnCreateModalHome(),
      { once: true }
    )

    if (this.createNameElement) {
      this.createModalElement.addEventListener(
        'shown.bs.modal',
        () => this.createNameElement.focus(),
        { once: true }
      )
    }
    modal.show()
  }

  async createWatchlist(event) {
    event.preventDefault()

    const name = this.createNameElement ? this.createNameElement.value.trim() : ''
    this.hideCreateError()

    if (!name) {
      this.showCreateError('Please enter a name.')
      return
    }

    this.setCreateSubmitting(true)

    try {
      const response = await fetch('/watchlists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ watchlist: { name } })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        // 403 = tier cap. The message and the numbers in it come from the
        // server (Plan::FEATURES) — never restate a limit here.
        this.showCreateError(data.error || 'Could not create the watchlist.', data.upgrade_url)
        return
      }

      this.addWatchlistOption(data.id, data.name)
      this.closeCreateModal()
    } catch (error) {
      console.error('Error creating watchlist:', error)
      this.showCreateError('Could not create the watchlist.')
    } finally {
      this.setCreateSubmitting(false)
    }
  }

  // Insert the new watchlist into the select (alphabetically, matching the
  // server's order(:name)), reveal the select if this was the first one, and
  // select it so the checkbox column appears ready to use.
  addWatchlistOption(id, name) {
    if (!this.hasWatchlistSelectTarget || !id) return

    const select = this.watchlistSelectTarget
    const option = document.createElement('option')
    option.value = String(id)
    option.textContent = name

    const successor = Array.from(select.options)
      .slice(1)
      .find(existing => existing.textContent.localeCompare(name, undefined, { sensitivity: 'base' }) > 0)
    select.insertBefore(option, successor || null)

    select.classList.remove('d-none')
    if (this.hasCreateLabelTarget) this.createLabelTarget.classList.add('d-none')

    this.watchlistsValue = [...(this.watchlistsValue || []), { id, name }]

    select.value = String(id)
    select.dispatchEvent(new Event('change', { bubbles: true }))
  }

  closeCreateModal() {
    if (!this.createModalElement) return
    window.bootstrap?.Modal?.getInstance(this.createModalElement)?.hide()
  }

  setCreateSubmitting(submitting) {
    if (this.createSubmitElement) this.createSubmitElement.disabled = submitting
  }

  showCreateError(message, upgradeUrl) {
    if (!this.createErrorElement) return

    this.createErrorElement.textContent = message
    if (upgradeUrl) {
      this.createErrorElement.appendChild(document.createTextNode(' '))
      const link = document.createElement('a')
      link.href = upgradeUrl
      link.className = 'alert-link fw-bold'
      link.textContent = 'View plans →'
      this.createErrorElement.appendChild(link)
    }
    this.createErrorElement.classList.remove('d-none')
  }

  hideCreateError() {
    if (!this.createErrorElement) return
    this.createErrorElement.textContent = ''
    this.createErrorElement.classList.add('d-none')
  }

  // Toggle individual stock
  async toggleStock(event) {
    const checkbox = event.target
    const ticker = checkbox.dataset.ticker
    const isChecked = checkbox.checked

    if (!this.watchlistIdValue) {
      checkbox.checked = !isChecked // Revert
      alert('Please select a watchlist first')
      return
    }

    try {
      if (isChecked) {
        await this.addStock(ticker)
        this.checkedTickersValue = [...this.checkedTickersValue, ticker]
      } else {
        await this.removeStock(ticker)
        this.checkedTickersValue = this.checkedTickersValue.filter(t => t !== ticker)
      }
      this.updateSelectAllState()
    } catch (error) {
      // Revert checkbox on error
      checkbox.checked = !isChecked
      if (error.upgradeRequired) {
        this.showUpgradePrompt(error.message)
      } else {
        console.error('Error toggling stock:', error)
      }
    }
  }

  async addStock(ticker) {
    const response = await fetch(`/watchlists/${this.watchlistIdValue}/watchlist_stocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({ ticker })
    })

    if (response.status === 403) {
      // Tier-paywall (free user hit the per-watchlist stock cap). Read the
      // body and throw an error tagged so the caller can render an inline
      // upgrade prompt instead of just reverting the checkbox silently.
      const data = await response.json().catch(() => ({}))
      const error = new Error(data.error || 'This action requires a paid plan.')
      error.upgradeRequired = true
      throw error
    }

    if (!response.ok) throw new Error('Failed to add stock')

    const data = await response.json()
    this.updateStockCount(data.count)
    return data
  }

  async removeStock(ticker) {
    const response = await fetch(`/watchlists/${this.watchlistIdValue}/watchlist_stocks/${encodeURIComponent(ticker)}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfToken
      }
    })

    if (!response.ok) throw new Error('Failed to remove stock')

    const data = await response.json()
    this.updateStockCount(data.count)
    return data
  }

  // Toggle select all - operates on ALL filtered results
  async toggleSelectAll(event) {
    const isChecked = event.target.checked

    if (!this.watchlistIdValue) {
      event.target.checked = false
      alert('Please select a watchlist first')
      return
    }

    // Get all tickers from visible rows
    const allTickers = this.getAllVisibleTickers()

    try {
      let result
      if (isChecked) {
        result = await this.bulkAddStocks(allTickers)
      } else {
        result = await this.bulkRemoveStocks(allTickers)
      }

      // Partial success — the server accepted some stocks but rejected the
      // rest because the free-tier per-watchlist cap was reached.
      if (result && result.upgrade_required && result.rejected_count > 0) {
        // stock_limit comes from Plan::FEATURES via the server — don't restate
        // the number here, it would drift the moment the plan changes.
        const limit = result.stock_limit
        this.showUpgradePrompt(
          limit
            ? `Your plan limits each watchlist to ${limit} stocks — ${result.rejected_count} were not added.`
            : `${result.rejected_count} stocks were not added because this watchlist is full.`
        )
      }

      // Refresh to get updated state
      await this.fetchWatchlistTickers()
    } catch (error) {
      event.target.checked = !isChecked
      if (error.upgradeRequired) {
        this.showUpgradePrompt(error.message)
      } else {
        console.error('Error in select all:', error)
      }
    }
  }

  async bulkAddStocks(tickers) {
    const response = await fetch(`/watchlists/${this.watchlistIdValue}/watchlist_stocks/bulk_add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({ tickers })
    })

    if (response.status === 403) {
      const data = await response.json().catch(() => ({}))
      const error = new Error(data.error || 'This action requires a paid plan.')
      error.upgradeRequired = true
      throw error
    }

    if (!response.ok) throw new Error('Failed to bulk add stocks')

    const data = await response.json()
    this.updateStockCount(data.count)
    return data
  }

  async bulkRemoveStocks(tickers) {
    const response = await fetch(`/watchlists/${this.watchlistIdValue}/watchlist_stocks/bulk_remove`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({ tickers })
    })

    if (!response.ok) throw new Error('Failed to bulk remove stocks')

    const data = await response.json()
    this.updateStockCount(data.count)
    return data
  }

  // Keyboard toggle handler (from lightweight-chart X/Enter key)
  handleKeyboardToggle(event) {
    const ticker = event.detail?.ticker
    if (!ticker || !this.watchlistIdValue) return

    // Find the checkbox for this ticker and toggle it
    const checkbox = this.element.querySelector(`input.watchlist-row-checkbox[data-ticker="${ticker}"]`)
    if (checkbox) {
      checkbox.checked = !checkbox.checked
      checkbox.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // Stop click propagation (prevent row selection when clicking checkbox)
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Render an inline upgrade banner when the free-tier per-watchlist stock
  // cap is hit. We dynamically inject a Bootstrap alert above the table so
  // the user gets a clear path to /subscriptions/pricing without a page
  // reload. The banner replaces any previous one so repeat triggers don't
  // pile up.
  showUpgradePrompt(message) {
    const existing = this.element.querySelector('.upgrade-prompt-toast')
    if (existing) existing.remove()

    const banner = document.createElement('div')
    banner.className = 'upgrade-prompt-toast alert alert-primary alert-dismissible fade show m-3'
    banner.setAttribute('role', 'alert')

    const text = document.createTextNode(message + ' ')
    banner.appendChild(text)

    const link = document.createElement('a')
    link.href = '/subscriptions/pricing'
    link.className = 'alert-link fw-bold'
    link.textContent = 'View plans →'
    banner.appendChild(link)

    const dismiss = document.createElement('button')
    dismiss.type = 'button'
    dismiss.className = 'btn-close'
    dismiss.setAttribute('data-bs-dismiss', 'alert')
    dismiss.setAttribute('aria-label', 'Close')
    banner.appendChild(dismiss)

    this.element.prepend(banner)
  }

  // Handle new rows added by infinite scroll
  handleNewRows() {
    if (this.watchlistIdValue) {
      // Show checkboxes for new rows and update their state
      this.showCheckboxes()
      this.updateAllCheckboxes()
    }
  }

  // UI Helpers
  showCheckboxes() {
    if (this.hasCheckboxHeaderTarget) {
      this.checkboxHeaderTarget.classList.remove('d-none')
    }
    this.checkboxCellTargets.forEach(cell => {
      cell.classList.remove('d-none')
    })
    if (this.hasKeyboardHintTarget) {
      this.keyboardHintTarget.classList.remove('d-none')
    }
  }

  hideCheckboxes() {
    if (this.hasCheckboxHeaderTarget) {
      this.checkboxHeaderTarget.classList.add('d-none')
    }
    this.checkboxCellTargets.forEach(cell => {
      cell.classList.add('d-none')
    })
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }
    if (this.hasStockCountTarget) {
      this.stockCountTarget.classList.add('d-none')
    }
    if (this.hasKeyboardHintTarget) {
      this.keyboardHintTarget.classList.add('d-none')
    }
  }

  updateAllCheckboxes() {
    const checkboxes = this.element.querySelectorAll('input.watchlist-row-checkbox')
    checkboxes.forEach(checkbox => {
      const ticker = checkbox.dataset.ticker
      checkbox.checked = this.checkedTickersValue.includes(ticker)
    })
    this.updateSelectAllState()
  }

  updateSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget) return

    const checkboxes = this.element.querySelectorAll('input.watchlist-row-checkbox')
    if (checkboxes.length === 0) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
      return
    }

    const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length

    if (checkedCount === 0) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    } else if (checkedCount === checkboxes.length) {
      this.selectAllCheckboxTarget.checked = true
      this.selectAllCheckboxTarget.indeterminate = false
    } else {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = true
    }
  }

  updateStockCount(count) {
    if (this.hasStockCountTarget) {
      this.stockCountTarget.textContent = `${count}`
      this.stockCountTarget.classList.toggle('d-none', count === 0 && !this.watchlistIdValue)
      if (this.watchlistIdValue) {
        this.stockCountTarget.classList.remove('d-none')
      }
    }
  }

  getAllVisibleTickers() {
    const checkboxes = this.element.querySelectorAll('input.watchlist-row-checkbox')
    return Array.from(checkboxes).map(cb => cb.dataset.ticker)
  }

  // Download action - downloads watchlist if selected, otherwise downloads filtered results
  download(event) {
    event.preventDefault()

    if (this.watchlistIdValue) {
      // Watchlist is selected - download watchlist tickers
      window.location.href = `/watchlists/${this.watchlistIdValue}/download`
    } else {
      // No watchlist selected - download filtered results
      const defaultUrl = event.currentTarget.dataset.defaultUrl
      if (defaultUrl) {
        window.location.href = defaultUrl
      }
    }
  }
}
